import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  WarningAmber as WarningIcon,
  ErrorOutline as ErrorIcon,
  CheckCircleOutline as OkIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageContainer from '../components/common/PageContainer';
import SectionHeader from '../components/common/SectionHeader';
import StatusChip from '../components/common/StatusChip';
import { dashboardService } from '../services/dashboardService';
import { getHomePath } from '../config/navigation';

const BRAND = '#800020';
const INK = '#0e141b';
const MUTED = '#4e7397';
const BORDER = '#d0dbe7';

// Una sincronización exitosa más vieja que esto se considera desactualizada
const TANGO_STALE_HOURS = 26;

const PIPELINE = [
  { status: 'borrador', label: 'Borrador' },
  { status: 'confirmado', label: 'Confirmado' },
  { status: 'en_elaboracion', label: 'En elaboración' },
  { status: 'listo', label: 'Listo p/ facturar' },
  { status: 'facturado', label: 'Facturado' },
  { status: 'entregado', label: 'Entregado' },
];

const TRIP_STATUS = {
  borrador: 'Borrador',
  en_curso: 'En curso',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
};

const money = (v) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(v || 0);

const num = (v) => new Intl.NumberFormat('es-AR').format(v || 0);

const parseDay = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const shortDay = (iso) =>
  parseDay(iso).toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric' });

const longDay = (iso) =>
  parseDay(iso).toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });

const dateTime = (iso) =>
  iso ? new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const hoursSince = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 36e5 : Infinity);

const Panel = ({ title, action, children, sx }) => (
  <Card variant="outlined" sx={{ borderColor: BORDER, p: 2.5, height: '100%', ...sx }}>
    {(title || action) && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1rem' }}>{title}</Typography>
        {action}
      </Box>
    )}
    {children}
  </Card>
);

const PanelLink = ({ to, label = 'Ver todo' }) => {
  const navigate = useNavigate();
  return (
    <Button
      size="small"
      endIcon={<ChevronRightIcon />}
      onClick={() => navigate(to)}
      sx={{ color: MUTED, fontWeight: 600, '&:hover': { color: BRAND, bgcolor: 'transparent' } }}
    >
      {label}
    </Button>
  );
};

const StatTile = ({ label, value, hint, to }) => {
  const navigate = useNavigate();
  const content = (
    <Box sx={{ p: 2.5 }}>
      <Typography sx={{ color: MUTED, fontSize: '0.8125rem', fontWeight: 500, mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ color: INK, fontSize: '1.75rem', fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ color: MUTED, fontSize: '0.75rem', mt: 0.5 }}>{hint}</Typography>
      )}
    </Box>
  );
  return (
    <Card variant="outlined" sx={{ borderColor: BORDER, height: '100%' }}>
      {to ? <CardActionArea onClick={() => navigate(to)} sx={{ height: '100%' }}>{content}</CardActionArea> : content}
    </Card>
  );
};

const MiniStat = ({ label, value, warn, text }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
    <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>{label}</Typography>
    <Stack direction="row" spacing={0.5} alignItems="center">
      {warn && <WarningIcon sx={{ fontSize: 16, color: '#ed6c02' }} />}
      <Typography sx={{ color: INK, fontSize: '0.875rem', fontWeight: 700 }}>{text ?? num(value)}</Typography>
    </Stack>
  </Box>
);

// Alertas que requieren atención. Cada una lleva ícono + texto (nunca solo color).
const buildAlerts = (data) => {
  const alerts = [];
  const { orders, clients, products, tango } = data;

  if (tango) {
    const last = tango.last_sync;
    if (!last) {
      alerts.push({ severity: 'warning', text: 'Nunca se sincronizó con Tango.', to: '/settings' });
    } else if (last.status === 'failed') {
      alerts.push({ severity: 'error', text: `La última sincronización con Tango falló (${dateTime(last.start_time)}).`, to: '/settings' });
    } else if (hoursSince(tango.last_success?.end_time || tango.last_success?.start_time) > TANGO_STALE_HOURS) {
      alerts.push({ severity: 'warning', text: `La última sincronización exitosa con Tango fue el ${dateTime(tango.last_success?.start_time)}.`, to: '/settings' });
    }
  }
  if (orders?.delivery.overdue) {
    alerts.push({ severity: 'error', text: `${num(orders.delivery.overdue)} pedido(s) con fecha de entrega vencida.`, to: '/production' });
  }
  if (orders?.by_status.listo) {
    alerts.push({ severity: 'info', text: `${num(orders.by_status.listo)} pedido(s) listos esperando facturación.`, to: '/production/billing' });
  }
  if (data.trips?.orders_awaiting_trip) {
    alerts.push({
      severity: 'info',
      text: `${num(data.trips.orders_awaiting_trip)} pedido(s) mayoristas sin viaje asignado en ${num(data.trips.cities_awaiting_trip)} ciudad(es).`,
      to: '/trips/create',
    });
  }
  if (clients?.credit_blocked) {
    alerts.push({ severity: 'warning', text: `${num(clients.credit_blocked)} cliente(s) con crédito bloqueado.`, to: '/clients' });
  }
  if (products?.wholesale_without_price) {
    alerts.push({ severity: 'warning', text: `${num(products.wholesale_without_price)} producto(s) mayoristas sin precio mayorista.`, to: '/products' });
  }
  return alerts;
};

const AlertsPanel = ({ alerts }) => {
  const navigate = useNavigate();
  if (alerts.length === 0) {
    return (
      <Alert icon={<OkIcon />} severity="success" sx={{ mb: 3, borderRadius: '0.75rem' }}>
        Todo en orden: no hay pendientes que requieran atención.
      </Alert>
    );
  }
  return (
    <Stack spacing={1} sx={{ mb: 3 }}>
      {alerts.map((a) => (
        <Alert
          key={a.text}
          severity={a.severity}
          icon={a.severity === 'error' ? <ErrorIcon /> : undefined}
          sx={{ borderRadius: '0.75rem', alignItems: 'center' }}
          action={a.to && (
            <Button color="inherit" size="small" onClick={() => navigate(a.to)} sx={{ fontWeight: 600 }}>
              Ver
            </Button>
          )}
        >
          {a.text}
        </Alert>
      ))}
    </Stack>
  );
};

// Pedidos creados por día (una sola serie). Tooltip por barra con el detalle.
const OrdersChart = ({ series }) => {
  const max = Math.max(1, ...series.map((d) => d.count));
  const totalCount = series.reduce((s, d) => s + d.count, 0);
  const totalAmount = series.reduce((s, d) => s + d.total, 0);
  const CHART_H = 160;

  return (
    <Box>
      <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>Pedidos</Typography>
          <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.25rem' }}>{num(totalCount)}</Typography>
        </Box>
        <Box>
          <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>Monto</Typography>
          <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.25rem' }}>{money(totalAmount)}</Typography>
        </Box>
      </Stack>
      <Box
        role="img"
        aria-label={`Pedidos por día en los últimos ${series.length} días: ${series.map((d) => `${shortDay(d.date)} ${d.count}`).join(', ')}`}
        sx={{ position: 'relative', height: CHART_H, borderBottom: `1px solid ${BORDER}` }}
      >
        {/* Línea de referencia del máximo */}
        <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, borderTop: `1px dashed ${BORDER}` }} />
        <Typography sx={{ position: 'absolute', right: 0, top: -18, fontSize: '0.6875rem', color: MUTED }}>
          máx. {max}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%', gap: '2px' }}>
          {series.map((d) => (
            <Tooltip
              key={d.date}
              arrow
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', textTransform: 'capitalize' }}>{longDay(d.date)}</Typography>
                  <Typography sx={{ fontSize: '0.75rem' }}>{d.count} pedido(s) · {money(d.total)}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', opacity: 0.8 }}>Público {d.retail} · Mayorista {d.wholesale}</Typography>
                </Box>
              }
            >
              {/* Zona de hover de altura completa; la barra se dibuja adentro */}
              <Box sx={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', cursor: 'default', '&:hover > div': { bgcolor: '#a00030' } }}>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 28,
                    mx: 'auto',
                    height: d.count ? `${(d.count / max) * 100}%` : 2,
                    minHeight: 2,
                    bgcolor: d.count ? BRAND : BORDER,
                    borderRadius: '4px 4px 0 0',
                    transition: 'background-color 120ms',
                  }}
                />
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: '2px', mt: 0.75 }}>
        {series.map((d, i) => (
          <Typography
            key={d.date}
            sx={{
              flex: 1,
              textAlign: 'center',
              fontSize: '0.6875rem',
              color: MUTED,
              // En pantallas chicas se muestra una etiqueta cada dos días
              visibility: { xs: i % 2 === series.length % 2 ? 'visible' : 'hidden', md: 'visible' },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {shortDay(d.date)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

const Pipeline = ({ byStatus }) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 1.5 }}>
      {PIPELINE.map((step) => (
        <Card key={step.status} variant="outlined" sx={{ borderColor: BORDER }}>
          <CardActionArea onClick={() => navigate('/production')} sx={{ p: 1.5 }}>
            <StatusChip status={step.status} label={step.label} sx={{ mb: 1 }} />
            <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.1 }}>
              {num(byStatus[step.status])}
            </Typography>
          </CardActionArea>
        </Card>
      ))}
    </Box>
  );
};

const RecentOrders = ({ orders }) => {
  const navigate = useNavigate();
  if (!orders.length) {
    return <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>Todavía no hay pedidos.</Typography>;
  }
  return (
    <Stack divider={<Box sx={{ borderTop: `1px solid ${BORDER}` }} />}>
      {orders.map((o) => (
        <Box
          key={o.id}
          onClick={() => navigate(`/production/${o.id}`)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: INK, fontWeight: 600, fontSize: '0.875rem' }} noWrap>
              {o.order_number} · {o.client_name || 'Sin cliente'}
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>
              {o.order_type === 'wholesale' ? 'Mayorista' : 'Público'} · {dateTime(o.created_at)}
            </Typography>
          </Box>
          <Typography sx={{ color: INK, fontWeight: 700, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{money(o.total)}</Typography>
          <StatusChip status={o.status} />
        </Box>
      ))}
    </Stack>
  );
};

const UpcomingTrips = ({ trips }) => {
  const navigate = useNavigate();
  if (!trips.length) {
    return <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>No hay viajes pendientes.</Typography>;
  }
  return (
    <Stack divider={<Box sx={{ borderTop: `1px solid ${BORDER}` }} />}>
      {trips.map((t) => (
        <Box
          key={t.id}
          onClick={() => navigate(`/trips/${t.id}`)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: INK, fontWeight: 600, fontSize: '0.875rem' }} noWrap>{t.city}</Typography>
            <Typography sx={{ color: MUTED, fontSize: '0.75rem', textTransform: 'capitalize' }}>
              {longDay(t.date)} · {t.total_orders} pedido(s)
            </Typography>
          </Box>
          <StatusChip
            status={t.status}
            label={TRIP_STATUS[t.status]}
            sx={t.status === 'en_curso' ? { bgcolor: '#e3f2fd', color: '#1976d2' } : {}}
          />
        </Box>
      ))}
    </Stack>
  );
};

const TangoPanel = ({ tango }) => {
  const last = tango.last_sync;
  const ok = last?.status === 'success';
  return (
    <Panel title="Sincronización Tango" action={<PanelLink to="/settings" label="Configurar" />}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        {last ? (
          ok ? <OkIcon sx={{ color: '#2e7d32' }} /> : <ErrorIcon sx={{ color: last.status === 'failed' ? '#c62828' : '#ed6c02' }} />
        ) : (
          <WarningIcon sx={{ color: '#ed6c02' }} />
        )}
        <Typography sx={{ color: INK, fontWeight: 600, fontSize: '0.875rem' }}>
          {!last ? 'Sin sincronizaciones' : ok ? 'Última sincronización OK' : last.status === 'failed' ? 'Última sincronización falló' : 'Sincronización en curso'}
        </Typography>
      </Stack>
      <MiniStat label="Última ejecución" text={dateTime(last?.start_time)} />
      <MiniStat label="Éxitos (7 días)" value={tango.last_week.success} />
      <MiniStat label="Fallos (7 días)" value={tango.last_week.failed} warn={tango.last_week.failed > 0} />
      <MiniStat label="Clientes replicados" value={tango.replica.clients} />
      <MiniStat label="Artículos replicados" value={tango.replica.articles} />
    </Panel>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const isWholesale = !!user?.is_wholesale_client;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const load = useCallback(async () => {
    setError('');
    try {
      setData(await dashboardService.getSummary());
    } catch (e) {
      setError('No se pudo cargar el estado general. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isWholesale) return undefined;
    load();
    // Refresco automático cada 2 minutos
    const id = setInterval(load, 120000);
    return () => clearInterval(id);
  }, [load, isWholesale]);

  const { orders, trips, clients, products, users } = data || {};

  // Los mayoristas del portal no tienen acceso al Dashboard
  if (isWholesale) {
    return <Navigate to={getHomePath(user)} replace />;
  }

  return (
    <PageContainer loading={loading} onLogout={handleLogout} maxWidth="xl">
      <SectionHeader
        title="Dashboard"
        subtitle={data ? `Estado general al ${dateTime(data.generated_at)}` : 'Estado general del sistema'}
        actions={[{ label: 'Actualizar', icon: <RefreshIcon />, variant: 'outlined', onClick: () => { setLoading(true); load(); } }]}
      />

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {data && (
        <>
          <AlertsPanel alerts={buildAlerts(data)} />

          {/* Indicadores principales */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {orders && (
              <>
                <Grid item xs={6} md={3}>
                  <StatTile
                    label="Pedidos abiertos"
                    value={num(orders.open_total)}
                    hint={`Público ${num(orders.open_by_type.retail)} · Mayorista ${num(orders.open_by_type.wholesale)}`}
                    to="/production"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatTile
                    label="Pedidos de hoy"
                    value={num(orders.today.created)}
                    hint={money(orders.today.created_total)}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatTile
                    label="Entregas para hoy"
                    value={num(orders.delivery.due_today)}
                    hint={`Mañana ${num(orders.delivery.due_tomorrow)} · Entregados hoy ${num(orders.today.delivered)}`}
                    to="/production"
                  />
                </Grid>
              </>
            )}
            {trips && (
              <Grid item xs={6} md={3}>
                <StatTile
                  label="Viajes activos"
                  value={num(trips.by_status.borrador + trips.by_status.en_curso)}
                  hint={`${num(trips.by_status.en_curso)} en curso · ${num(trips.pending_stops)} paradas pendientes`}
                  to="/trips"
                />
              </Grid>
            )}
          </Grid>

          {orders && (
            <Panel title="Flujo de pedidos" action={<PanelLink to="/production" label="Ir a Producción" />} sx={{ mb: 3, height: 'auto' }}>
              <Pipeline byStatus={orders.by_status} />
              {orders.special_production_open > 0 && (
                <Typography sx={{ color: MUTED, fontSize: '0.8125rem', mt: 1.5 }}>
                  {num(orders.special_production_open)} pedido(s) abiertos requieren producción especial.
                </Typography>
              )}
            </Panel>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {orders && (
              <Grid item xs={12} lg={trips ? 8 : 12}>
                <Panel title={`Pedidos por día (últimos ${orders.series.length} días)`}>
                  <OrdersChart series={orders.series} />
                </Panel>
              </Grid>
            )}
            {trips && (
              <Grid item xs={12} lg={orders ? 4 : 12}>
                <Panel title="Próximos viajes" action={<PanelLink to="/trips" />}>
                  <UpcomingTrips trips={trips.upcoming} />
                </Panel>
              </Grid>
            )}
          </Grid>

          {orders && (
            <Panel title="Últimos pedidos" action={<PanelLink to="/production" />} sx={{ mb: 3, height: 'auto' }}>
              <RecentOrders orders={orders.recent} />
            </Panel>
          )}

          <Grid container spacing={2}>
            {clients && (
              <Grid item xs={12} sm={6} lg={3}>
                <Panel title="Clientes" action={<PanelLink to="/clients" />}>
                  <MiniStat label="Total" value={clients.total} />
                  <MiniStat label="Público" value={clients.retail} />
                  <MiniStat label="Mayoristas" value={clients.wholesale} />
                  <MiniStat label="Con acceso al portal" value={clients.with_portal_user} />
                  <MiniStat label="Crédito en revisión" value={clients.credit_review} />
                  <MiniStat label="Crédito bloqueado" value={clients.credit_blocked} warn={clients.credit_blocked > 0} />
                  <MiniStat label="Mayoristas sin ciudad de reparto" value={clients.without_delivery_city} warn={clients.without_delivery_city > 0} />
                </Panel>
              </Grid>
            )}
            {products && (
              <Grid item xs={12} sm={6} lg={3}>
                <Panel title="Productos" action={<PanelLink to="/products" />}>
                  <MiniStat label="Total" value={products.total} />
                  <MiniStat label="Disponibles al público" value={products.retail} />
                  <MiniStat label="Disponibles a mayoristas" value={products.wholesale} />
                  <MiniStat label="Sin precio mayorista" value={products.wholesale_without_price} warn={products.wholesale_without_price > 0} />
                  <MiniStat label="Sin código Tango" value={products.without_tango_code} />
                </Panel>
              </Grid>
            )}
            {trips && (
              <Grid item xs={12} sm={6} lg={3}>
                <Panel title="Reparto" action={<PanelLink to="/trips" />}>
                  <MiniStat label="Viajes hoy" value={trips.today} />
                  <MiniStat label="En borrador" value={trips.by_status.borrador} />
                  <MiniStat label="En curso" value={trips.by_status.en_curso} />
                  <MiniStat label="Finalizados" value={trips.by_status.finalizado} />
                  <MiniStat label="Pedidos sin viaje" value={trips.orders_awaiting_trip} />
                  <MiniStat label="Ciudades habilitadas" value={trips.cities_available} />
                </Panel>
              </Grid>
            )}
            {data.tango && (
              <Grid item xs={12} sm={6} lg={3}>
                <TangoPanel tango={data.tango} />
              </Grid>
            )}
            {users && (
              <Grid item xs={12} sm={6} lg={3}>
                <Panel title="Usuarios" action={<PanelLink to="/users" />}>
                  <MiniStat label="Activos" value={users.active} />
                  <MiniStat label="Administradores" value={users.staff} />
                  <MiniStat label="Mayoristas (portal)" value={users.wholesale_portal} />
                </Panel>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </PageContainer>
  );
};

export default Dashboard;
